import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ChevronLeft, Save, Clock, User, Calendar, Tag, AlertCircle,
  MessageSquare, Plus, Trash2, Mail, Send, Monitor, Wifi, WifiOff, AlertTriangle,
  Link2, Unlink, Search, RotateCcw, ExternalLink, Sparkles, Bot, Lightbulb, List,
  Wand2, Copy, FileText, Paperclip, Download, Image, File, Award, TrendingUp, 
  CheckCircle, XCircle, RefreshCw, UserPlus, Loader2, GitMerge, BookOpen,
  ThumbsUp, ThumbsDown
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';

const STATUS_COLORS = {
  'Open': 'bg-blue-100 text-blue-700',
  'In Progress': 'bg-yellow-100 text-yellow-700',
  'Waiting': 'bg-orange-100 text-orange-700',
  'Resolved': 'bg-green-100 text-green-700',
  'Closed': 'bg-gray-100 text-gray-600',
  'Merged': 'bg-violet-100 text-violet-700',
};

const PRIORITY_COLORS = {
  'Low': 'bg-gray-100 text-gray-600',
  'Medium': 'bg-blue-100 text-blue-700',
  'High': 'bg-orange-100 text-orange-700',
  'Critical': 'bg-red-100 text-red-700',
};

const formatTime = (minutes) => {
  if (!minutes) return '0m';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours < 24) return `${hours}h ${mins}m`;
  const days = Math.floor(hours / 24);
  return `${days}d ${hours % 24}h`;
};

const formatDate = (isoString) => {
  if (!isoString) return '-';
  return new Date(isoString).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit'
  });
};

// Helper to get file icon based on content type
const getFileIcon = (contentType, filename) => {
  if (contentType?.startsWith('image/')) return Image;
  if (contentType?.includes('pdf')) return FileText;
  return File;
};

// Helper to format file size
const formatFileSize = (bytes) => {
  if (!bytes) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

// Attachment display component
const AttachmentList = ({ attachments, apiBase }) => {
  if (!attachments || attachments.length === 0) return null;
  
  return (
    <div className="mt-2 pt-2 border-t border-dashed">
      <div className="flex items-center gap-1 text-xs text-gray-500 mb-1">
        <Paperclip className="h-3 w-3" />
        <span>{attachments.length} attachment{attachments.length > 1 ? 's' : ''}</span>
      </div>
      <div className="flex flex-wrap gap-2">
        {attachments.map((att, idx) => {
          const FileIcon = getFileIcon(att.content_type, att.filename);
          const isImage = att.content_type?.startsWith('image/');
          const attachmentUrl = `${apiBase}/api/ticketing/managed/attachment/${att.attachment_id}`;
          
          return (
            <a
              key={idx}
              href={attachmentUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-2 py-1.5 bg-white border rounded hover:bg-gray-50 hover:border-blue-300 transition-colors group"
              data-testid={`attachment-${att.attachment_id}`}
            >
              {isImage ? (
                <img 
                  src={attachmentUrl} 
                  alt={att.filename}
                  className="h-8 w-8 object-cover rounded"
                />
              ) : (
                <FileIcon className="h-4 w-4 text-gray-400 group-hover:text-blue-500" />
              )}
              <div className="flex flex-col">
                <span className="text-xs font-medium text-gray-700 group-hover:text-blue-600 truncate max-w-32">
                  {att.filename}
                </span>
                {att.size && (
                  <span className="text-xs text-gray-400">{formatFileSize(att.size)}</span>
                )}
              </div>
              <Download className="h-3 w-3 text-gray-300 group-hover:text-blue-500" />
            </a>
          );
        })}
      </div>
    </div>
  );
};

// AI Quality Scorecard Component
const AIQualityScorecard = ({ ticket, onReviewComplete }) => {
  const [reviewing, setReviewing] = useState(false);
  const [expanded, setExpanded] = useState(false);
  
  const review = ticket.ai_review;
  const score = ticket.quality_score;
  
  const getScoreColor = (s) => {
    if (s >= 90) return 'text-green-600';
    if (s >= 80) return 'text-blue-600';
    if (s >= 70) return 'text-yellow-600';
    if (s >= 60) return 'text-orange-600';
    return 'text-red-600';
  };
  
  const getGradeBg = (grade) => {
    const colors = {
      'A': 'bg-green-100 text-green-700 border-green-300',
      'B': 'bg-blue-100 text-blue-700 border-blue-300',
      'C': 'bg-yellow-100 text-yellow-700 border-yellow-300',
      'D': 'bg-orange-100 text-orange-700 border-orange-300',
      'F': 'bg-red-100 text-red-700 border-red-300'
    };
    return colors[grade] || 'bg-gray-100 text-gray-700 border-gray-300';
  };
  
  const handleTriggerReview = async () => {
    setReviewing(true);
    try {
      await api.reviewTicket(ticket.ticket_id);
      toast.success('AI review complete!');
      onReviewComplete?.();
    } catch (err) {
      console.error('Failed to review ticket:', err);
      toast.error('Failed to generate AI review');
    } finally {
      setReviewing(false);
    }
  };
  
  // Criteria breakdown (estimated based on our scoring system)
  const criteria = [
    { name: 'Documentation Quality', weight: 20, description: 'Were notes clear and thorough?' },
    { name: 'Troubleshooting Approach', weight: 25, description: 'Logical steps? Root cause addressed?' },
    { name: 'Efficiency', weight: 20, description: 'Was resolution time reasonable?' },
    { name: 'Communication', weight: 15, description: 'Was user kept informed?' },
    { name: 'Best Practices', weight: 20, description: 'IT best practices followed?' },
  ];

  if (!review && !score) {
    return (
      <Card className="border-purple-200 bg-purple-50/50">
        <CardHeader className="pb-3">
          <CardTitle className="text-sm flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Award className="h-4 w-4 text-purple-600" />
              AI Quality Review
            </div>
            <Button 
              size="sm" 
              onClick={handleTriggerReview}
              disabled={reviewing}
              className="gap-1 bg-purple-600 hover:bg-purple-700"
            >
              {reviewing ? (
                <>
                  <RefreshCw className="h-3 w-3 animate-spin" />
                  Analyzing...
                </>
              ) : (
                <>
                  <Sparkles className="h-3 w-3" />
                  Generate Review
                </>
              )}
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-gray-500 text-center py-4">
            Click "Generate Review" to get an AI assessment of how this ticket was handled.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-purple-200">
      <CardHeader className="pb-3">
        <CardTitle className="text-sm flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Award className="h-4 w-4 text-purple-600" />
            AI Quality Review
          </div>
          <div className="flex items-center gap-2">
            <div className={`text-2xl font-bold ${getScoreColor(score)}`}>
              {score}
            </div>
            <div className={`px-2 py-1 rounded border font-bold ${getGradeBg(review?.grade)}`}>
              {review?.grade || '?'}
            </div>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Summary */}
        {review?.summary && (
          <div className="p-3 bg-gray-50 rounded-lg">
            <p className="text-sm text-gray-700">{review.summary}</p>
          </div>
        )}
        
        {/* Criteria Breakdown */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-gray-500 uppercase">Scoring Criteria</span>
            <button 
              onClick={() => setExpanded(!expanded)}
              className="text-xs text-purple-600 hover:underline"
            >
              {expanded ? 'Hide Details' : 'Show Details'}
            </button>
          </div>
          <div className="space-y-2">
            {criteria.map((c, idx) => (
              <div key={idx}>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-gray-700">{c.name}</span>
                  <span className="text-xs text-gray-400">{c.weight} pts</span>
                </div>
                {expanded && (
                  <p className="text-xs text-gray-400 mt-0.5 ml-2">{c.description}</p>
                )}
              </div>
            ))}
          </div>
        </div>
        
        {/* Strengths */}
        {review?.strengths && (
          <div className="p-3 bg-green-50 rounded-lg border border-green-200">
            <div className="flex items-center gap-2 mb-1">
              <CheckCircle className="h-4 w-4 text-green-600" />
              <span className="text-sm font-semibold text-green-700">Strengths</span>
            </div>
            <p className="text-sm text-green-800">{review.strengths}</p>
          </div>
        )}
        
        {/* Improvements */}
        {review?.improvements && (
          <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
            <div className="flex items-center gap-2 mb-1">
              <TrendingUp className="h-4 w-4 text-amber-600" />
              <span className="text-sm font-semibold text-amber-700">Areas for Improvement</span>
            </div>
            <p className="text-sm text-amber-800">{review.improvements}</p>
          </div>
        )}
        
        {/* AI Comparison */}
        {review?.ai_comparison && (
          <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-center gap-2 mb-1">
              <Bot className="h-4 w-4 text-blue-600" />
              <span className="text-sm font-semibold text-blue-700">AI Comparison</span>
            </div>
            <p className="text-sm text-blue-800">{review.ai_comparison}</p>
          </div>
        )}
        
        {/* Reviewed timestamp */}
        {review?.reviewed_at && (
          <p className="text-xs text-gray-400 text-right">
            Reviewed: {new Date(review.reviewed_at).toLocaleString()}
          </p>
        )}
        
        {/* Re-review button */}
        <div className="pt-2 border-t">
          <Button 
            size="sm" 
            variant="outline"
            onClick={handleTriggerReview}
            disabled={reviewing}
            className="w-full gap-1"
          >
            {reviewing ? (
              <>
                <RefreshCw className="h-3 w-3 animate-spin" />
                Re-analyzing...
              </>
            ) : (
              <>
                <RefreshCw className="h-3 w-3" />
                Re-run AI Review
              </>
            )}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const TicketDetailPage = () => {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const noteFileInputRef = React.useRef(null);
  const [ticket, setTicket] = useState(null);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editData, setEditData] = useState({});
  const [newNote, setNewNote] = useState('');
  const [noteAttachments, setNoteAttachments] = useState([]);
  const [ccEmails, setCcEmails] = useState([]);
  const [ccInput, setCcInput] = useState('');
  const [azureUsers, setAzureUsers] = useState([]);
  const [showCcDropdown, setShowCcDropdown] = useState(false);
  const [sendingNote, setSendingNote] = useState(false);
  const [internalOnly, setInternalOnly] = useState(false);  // For internal notes that don't email
  const [timeEntry, setTimeEntry] = useState({ minutes: 15, notes: '' });
  const [showTimeDialog, setShowTimeDialog] = useState(false);
  
  // Get the current user's display name for notes
  const authorName = user?.name || user?.displayName || user?.email?.split('@')[0] || 'IT Staff';
  const [userDevices, setUserDevices] = useState([]);
  const [loadingDevices, setLoadingDevices] = useState(false);
  
  // Linked Asset state
  const [linkedAsset, setLinkedAsset] = useState(null);
  const [loadingAsset, setLoadingAsset] = useState(false);
  const [showLinkDialog, setShowLinkDialog] = useState(false);
  const [assetSearch, setAssetSearch] = useState('');
  const [assetResults, setAssetResults] = useState([]);
  const [searchingAssets, setSearchingAssets] = useState(false);
  
  // AI Guidance state
  const [analyzingWithAI, setAnalyzingWithAI] = useState(false);
  
  // Resolution note state
  const [showResolutionDialog, setShowResolutionDialog] = useState(false);
  const [suggestedResolution, setSuggestedResolution] = useState('');
  
  // NEW: AI Draft Response state
  const [generatingDraft, setGeneratingDraft] = useState(false);
  const [draftResponse, setDraftResponse] = useState('');
  
  // NEW: Duplicate Detection state
  const [duplicates, setDuplicates] = useState(null);
  const [checkingDuplicates, setCheckingDuplicates] = useState(false);
  const [resolutionNote, setResolutionNote] = useState('');
  const [generatingResolution, setGeneratingResolution] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState(null);

  // KB Article generation state
  const [generatingKB, setGeneratingKB] = useState(false);

  // KB Article suggestions state
  const [kbSuggestions, setKbSuggestions] = useState([]);
  const [loadingKBSuggestions, setLoadingKBSuggestions] = useState(false);
  const [kbFeedback, setKbFeedback] = useState({});  // { kb_id: true/false/null }

  // Onboarding from ticket
  const [showOnboardForm, setShowOnboardForm] = useState(false);
  const [onboardParsing, setOnboardParsing] = useState(false);
  const [onboardSubmitting, setOnboardSubmitting] = useState(false);
  const DEPARTMENTS = [
    'Finance', 'Accounting', 'Sales', 'Inside Sales', 'OSR', 'KAM',
    'Operations', 'Logistics', 'Supply Chain', 'Warehouse', 'Quality',
    'IT', 'HR', 'Marketing', 'Admin',
    'Emerging Markets', 'Beverage', 'Consumer', 'NBD', 'Procurement',
  ];
  const [onboardForm, setOnboardForm] = useState({
    full_name: '', email_alias: '', job_title: '', department: '',
    office_location: '', manager: '', start_date: '', cell_phone: '',
    personal_email: '', bc: false, bc_role: '', spiro: false,
    teams_phone: true, business_cards: false, website: false,
    jd_edwards: false, laptop_model: '',
  });

  useEffect(() => {
    fetchTicket();
    fetchSettings();
    fetchAzureUsers();
  }, [ticketId]);

  useEffect(() => {
    if (ticket) {
      fetchUserDevices();
      fetchKBSuggestions();
      if (ticket.linked_asset_id) {
        fetchLinkedAsset(ticket.linked_asset_id);
      }
    }
  }, [ticket?.ticket_id, ticket?.linked_asset_id]);

  const fetchTicket = async () => {
    setLoading(true);
    try {
      const res = await api.getManagedTicket(ticketId);
      setTicket(res.data);
      setEditData({
        status: res.data.status,
        assigned_to: res.data.assigned_to || '',
        priority: res.data.priority,
        category: res.data.category,
        created_by_name: res.data.created_by_name || '',
        created_by_email: res.data.created_by_email || '',
      });
    } catch (err) {
      console.error('Failed to fetch ticket:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchKBSuggestions = async () => {
    setLoadingKBSuggestions(true);
    try {
      const res = await api.suggestKBArticles(ticketId);
      setKbSuggestions(res.data.suggestions || []);
    } catch (err) {
      console.error('Failed to fetch KB suggestions:', err);
    } finally {
      setLoadingKBSuggestions(false);
    }
  };

  const handleKBFeedback = async (kbId, helpful) => {
    // Toggle off if same value clicked again
    const current = kbFeedback[kbId];
    if (current === helpful) return;
    setKbFeedback(prev => ({ ...prev, [kbId]: helpful }));
    try {
      await api.submitKBFeedback(kbId, ticketId, helpful);
      toast.success(helpful ? 'Marked as helpful' : 'Marked as not helpful');
    } catch (err) {
      console.error('Failed to submit KB feedback:', err);
    }
  };

  const fetchSettings = async () => {
    try {
      const res = await api.getTicketSettings();
      setSettings(res.data);
    } catch (err) {
      console.error('Failed to fetch settings:', err);
    }
  };

  const fetchAzureUsers = async () => {
    try {
      const res = await api.getAzureUsers();
      setAzureUsers(res.data.users || []);
    } catch (err) {
      console.error('Failed to fetch Azure users:', err);
    }
  };

  const fetchUserDevices = async () => {
    if (!ticket?.ticket_id) return;
    setLoadingDevices(true);
    try {
      const res = await api.getTicketDevices(ticket.ticket_id);
      setUserDevices(res.data.devices || []);
    } catch (err) {
      console.error('Failed to fetch user devices:', err);
    } finally {
      setLoadingDevices(false);
    }
  };

  const fetchLinkedAsset = async (assetId) => {
    setLoadingAsset(true);
    try {
      const res = await api.getAction1Endpoint(assetId);
      setLinkedAsset(res.data);
    } catch (err) {
      console.error('Failed to fetch linked asset:', err);
      setLinkedAsset(null);
    } finally {
      setLoadingAsset(false);
    }
  };

  const searchAssets = async (query) => {
    if (!query || query.length < 2) {
      setAssetResults([]);
      return;
    }
    setSearchingAssets(true);
    try {
      const res = await api.getAction1Endpoints({ search: query, limit: 10 });
      setAssetResults(res.data.endpoints || []);
    } catch (err) {
      console.error('Failed to search assets:', err);
    } finally {
      setSearchingAssets(false);
    }
  };

  const handleLinkAsset = async (asset) => {
    try {
      await api.linkTicketToAsset(asset.id, ticketId);
      setTicket(prev => ({ ...prev, linked_asset_id: asset.id }));
      setLinkedAsset(asset);
      setShowLinkDialog(false);
      setAssetSearch('');
      setAssetResults([]);
      toast.success(`Linked to ${asset.name}`);
    } catch (err) {
      toast.error('Failed to link asset');
    }
  };

  const handleUnlinkAsset = async () => {
    if (!window.confirm('Unlink this asset from the ticket?')) return;
    try {
      await api.updateManagedTicket(ticketId, { linked_asset_id: null });
      setTicket(prev => ({ ...prev, linked_asset_id: null }));
      setLinkedAsset(null);
      toast.success('Asset unlinked');
    } catch (err) {
      toast.error('Failed to unlink asset');
    }
  };

  const handleUpdate = async () => {
    setSaving(true);
    try {
      const res = await api.updateManagedTicket(ticketId, editData);
      setTicket(res.data);
      // Sync editData with actual saved values from backend
      // Critical: backend may redirect "Resolved" -> "Pending Approval"
      setEditData({
        status: res.data.status,
        assigned_to: res.data.assigned_to || '',
        priority: res.data.priority,
        category: res.data.category,
        created_by_name: res.data.created_by_name || '',
        created_by_email: res.data.created_by_email || '',
      });
      toast.success('Ticket updated successfully');
      
      // If ticket went to Pending Approval, poll for AI review result
      // so the user sees immediate feedback if auto-rejected or auto-approved
      if (res.data.status === 'Pending Approval') {
        toast.info('AI is reviewing this ticket...', { duration: 3000 });
        pollForReviewResult(ticketId);
      }
    } catch (err) {
      console.error('Failed to update ticket:', err);
      toast.error('Failed to update ticket: ' + (err.response?.data?.detail || err.message));
      // Reset editData to match the actual ticket state so the dropdown doesn't show a stale status
      setEditData({
        status: ticket.status,
        assigned_to: ticket.assigned_to || '',
        priority: ticket.priority,
        category: ticket.category,
        created_by_name: ticket.created_by_name || '',
        created_by_email: ticket.created_by_email || '',
      });
    } finally {
      setSaving(false);
    }
  };
  
  // Poll the ticket after Pending Approval to catch AI review results
  const pollForReviewResult = async (tid) => {
    const maxAttempts = 8;
    const delayMs = 2000; // 2 seconds between polls
    
    for (let i = 0; i < maxAttempts; i++) {
      await new Promise(r => setTimeout(r, delayMs));
      try {
        const res = await api.getManagedTicket(tid);
        const updated = res.data;
        
        if (updated.status !== 'Pending Approval') {
          // AI review has completed and changed the status
          setTicket(updated);
          setEditData({
            status: updated.status,
            assigned_to: updated.assigned_to || '',
            priority: updated.priority,
            category: updated.category,
            created_by_name: updated.created_by_name || '',
            created_by_email: updated.created_by_email || '',
          });
          
          if (updated.status === 'Closed') {
            toast.success(`Ticket auto-approved by AI (Score: ${updated.quality_score || '?'}/100) and closed!`, { duration: 6000 });
          } else if (updated.status === 'In Progress' && updated.approval_type === 'auto_rejected') {
            // Auto-rejected — show detailed feedback
            const review = updated.ai_review;
            const score = updated.quality_score || review?.score || '?';
            const issues = review?.issues || [];
            const improvements = review?.improvements || '';
            let msg = `AI Review rejected this ticket (Score: ${score}/100).\n`;
            if (issues.length > 0) {
              msg += issues.slice(0, 3).map(i => `• ${i.issue || i}`).join('\n');
            } else if (improvements) {
              msg += improvements;
            }
            toast.error(msg, { duration: 12000 });
          }
          return; // Done polling
        }
        
        // Check if AI review completed but status stayed Pending Approval (human review needed)
        if (updated.ai_review && updated.needs_human_review) {
          setTicket(updated);
          toast.info(`AI review complete (Score: ${updated.quality_score || '?'}/100). Awaiting human approval.`, { duration: 6000 });
          return;
        }
      } catch (err) {
        // Ignore polling errors, just continue
      }
    }
  };

  const handleAnalyzeWithAI = async () => {
    setAnalyzingWithAI(true);
    try {
      const res = await api.analyzeTicketWithAI(ticketId);
      if (res.data.success) {
        toast.success('AI analysis complete');
        fetchTicket(); // Refresh to get the AI guidance
      } else {
        toast.error(res.data.error || 'AI analysis failed');
      }
    } catch (err) {
      console.error('Failed to analyze with AI:', err);
      toast.error('Failed to analyze ticket');
    } finally {
      setAnalyzingWithAI(false);
    }
  };

  // NEW: Generate AI draft response
  const handleGenerateDraft = async () => {
    setGeneratingDraft(true);
    try {
      const res = await api.getDraftResponse(ticketId);
      if (res.data.success && res.data.draft) {
        setDraftResponse(res.data.draft);
        setNewNote(res.data.draft);
        toast.success('AI draft generated! Review and edit before sending.');
      } else {
        toast.error(res.data.error || 'Failed to generate draft');
      }
    } catch (err) {
      console.error('Failed to generate draft:', err);
      toast.error('Failed to generate AI draft');
    } finally {
      setGeneratingDraft(false);
    }
  };

  // NEW: Check for duplicate tickets
  const handleCheckDuplicates = async () => {
    setCheckingDuplicates(true);
    try {
      const res = await api.checkDuplicates(ticketId);
      setDuplicates(res.data);
      if (res.data.has_likely_duplicates) {
        toast.warning(`Found ${res.data.count} potential duplicate(s)!`);
      } else if (res.data.duplicates?.length > 0) {
        toast.info(`Found ${res.data.duplicates.length} similar tickets`);
      } else {
        toast.success('No duplicate tickets found');
      }
    } catch (err) {
      console.error('Failed to check duplicates:', err);
      toast.error('Failed to check for duplicates');
    } finally {
      setCheckingDuplicates(false);
    }
  };

  // Handle KB article generation
  const handleGenerateKB = async () => {
    setGeneratingKB(true);
    try {
      const res = await api.generateKBArticle(ticketId);
      if (res.data.success) {
        toast.success('KB article generated!');
        fetchTicket(); // Refresh to show linked KB
        // Navigate to the new article
        navigate(`/kb/${res.data.article.kb_id}`);
      } else {
        toast.error('Failed to generate KB article');
      }
    } catch (err) {
      console.error('Failed to generate KB article:', err);
      toast.error('Failed to generate KB article: ' + (err.response?.data?.detail || err.message));
    } finally {
      setGeneratingKB(false);
    }
  };

  // Handle status change - show AI resolution dialog when resolving
  const handleStatusChange = async (newStatus) => {
    if ((newStatus === 'Resolved' || newStatus === 'Closed') && ticket.status !== 'Resolved' && ticket.status !== 'Closed') {
      // Opening a ticket to be resolved - get AI suggestion
      setPendingStatusChange(newStatus);
      setShowResolutionDialog(true);
      setGeneratingResolution(true);
      
      try {
        const res = await api.suggestResolutionNote(ticketId);
        if (res.data.success && res.data.suggested_note) {
          setSuggestedResolution(res.data.suggested_note);
          setResolutionNote(res.data.suggested_note);
        } else {
          setSuggestedResolution('');
          setResolutionNote('');
        }
      } catch (err) {
        console.error('Failed to get AI suggestion:', err);
        setSuggestedResolution('');
        setResolutionNote('');
      } finally {
        setGeneratingResolution(false);
      }
    } else {
      // Normal status change
      setEditData(prev => ({ ...prev, status: newStatus }));
    }
  };

  // Confirm resolution with note
  const handleConfirmResolution = async () => {
    // Add resolution note if provided
    if (resolutionNote.trim()) {
      try {
        await api.addTicketNote(ticketId, {
          content: `**Resolution:** ${resolutionNote}`,
          author: 'IT Staff'
        });
      } catch (err) {
        console.error('Failed to add resolution note:', err);
      }
    }
    
    // Update status
    setEditData(prev => ({ ...prev, status: pendingStatusChange }));
    setShowResolutionDialog(false);
    setSuggestedResolution('');
    setResolutionNote('');
    setPendingStatusChange(null);
    
    toast.success('Resolution note added. Click Save to update ticket status.');
  };

  const handleCancelResolution = () => {
    setShowResolutionDialog(false);
    setSuggestedResolution('');
    setResolutionNote('');
    setPendingStatusChange(null);
  };

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    setSendingNote(true);
    try {
      // Use the new endpoint if there are attachments
      if (noteAttachments.length > 0) {
        const formData = new FormData();
        formData.append('content', newNote);
        formData.append('author', authorName);
        formData.append('internal_only', internalOnly);
        formData.append('cc_emails', ccEmails.join(','));
        noteAttachments.forEach(file => formData.append('files', file));
        
        await api.addTicketNoteWithAttachments(ticketId, formData);
      } else {
        await api.addTicketNote(ticketId, { 
          content: newNote, 
          author: authorName,
          cc_emails: ccEmails.length > 0 ? ccEmails : null,
          internal_only: internalOnly
        });
      }
      
      setNewNote('');
      setNoteAttachments([]);
      setCcEmails([]);
      setCcInput('');
      setInternalOnly(false);
      toast.success(internalOnly ? 'Internal note added' : 'Note added and email sent');
      fetchTicket();
    } catch (err) {
      console.error('Failed to add note:', err);
      toast.error('Failed to add note');
    } finally {
      setSendingNote(false);
    }
  };

  const handleDeleteNote = async (noteId) => {
    if (!window.confirm('Delete this note? This cannot be undone.')) return;
    try {
      await api.deleteTicketNote(ticketId, noteId);
      toast.success('Note deleted');
      fetchTicket();
    } catch (err) {
      console.error('Failed to delete note:', err);
      toast.error('Failed to delete note');
    }
  };

  const handleCcKeyDown = (e) => {
    if (e.key === 'Enter' && ccInput.trim()) {
      e.preventDefault();
      const email = ccInput.trim();
      if (email.includes('@') && !ccEmails.includes(email)) {
        setCcEmails([...ccEmails, email]);
        setCcInput('');
        setShowCcDropdown(false);
      }
    }
  };

  const handleNoteFileSelect = (e) => {
    const files = Array.from(e.target.files);
    const validFiles = files.filter(f => f.size <= 10 * 1024 * 1024); // 10MB limit
    if (validFiles.length < files.length) {
      toast.error('Some files exceed the 10MB limit');
    }
    setNoteAttachments(prev => [...prev, ...validFiles]);
    e.target.value = '';
  };

  const removeNoteAttachment = (index) => {
    setNoteAttachments(prev => prev.filter((_, i) => i !== index));
  };

  const handleLogTime = async () => {
    try {
      await api.logTicketTime(ticketId, timeEntry);
      setTimeEntry({ minutes: 15, notes: '' });
      setShowTimeDialog(false);
      fetchTicket();
    } catch (err) {
      console.error('Failed to log time:', err);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this ticket?')) return;
    try {
      await api.deleteManagedTicket(ticketId);
      navigate('/tickets/managed');
    } catch (err) {
      console.error('Failed to delete ticket:', err);
    }
  };

  const handleStartOnboarding = async () => {
    setOnboardParsing(true);
    try {
      const res = await api.parseTicketForOnboarding(ticketId);
      if (res.data.success && res.data.parsed) {
        const p = res.data.parsed;
        setOnboardForm({
          full_name: p.full_name || '', email_alias: p.email_alias || '',
          job_title: p.job_title || '', department: p.department || '',
          office_location: p.office_location || '', manager: p.manager || '',
          start_date: p.start_date || '', cell_phone: p.cell_phone || '',
          personal_email: p.personal_email || '', bc: p.bc || false,
          bc_role: p.bc_role || '', spiro: p.spiro || false,
          teams_phone: p.teams_phone !== false, business_cards: p.business_cards || false,
          website: p.website || false, jd_edwards: p.jd_edwards || false,
          laptop_model: p.laptop_model || '',
        });
        toast.success('Parsed new hire details from ticket');
      } else {
        toast.info(res.data.message || 'Could not auto-parse — fill in details manually');
      }
    } catch (err) {
      toast.error('Failed to parse ticket: ' + (err.response?.data?.detail || err.message));
    } finally {
      setOnboardParsing(false);
      setShowOnboardForm(true);
    }
  };

  const handleSubmitOnboarding = async () => {
    if (!onboardForm.full_name.trim() || !onboardForm.email_alias.trim()) {
      toast.error('Full name and email alias are required');
      return;
    }
    setOnboardSubmitting(true);
    try {
      const overrides = { ...onboardForm };
      Object.keys(overrides).forEach(k => {
        if (overrides[k] === '' || overrides[k] === null) delete overrides[k];
      });
      const res = await api.processOnboardingFromTicket(ticketId, overrides);
      toast.success(`Onboarding started! Ticket #${res.data.ticket_number}`);
      setShowOnboardForm(false);
      fetchTicket(); // Refresh to show linked onboarding ticket
    } catch (err) {
      toast.error('Onboarding failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setOnboardSubmitting(false);
    }
  };

  const isNewHireTicket = ticket && (
    ticket.ticket_type === 'New Hire' ||
    ticket.category === 'Onboarding' ||
    (ticket.summary || '').toUpperCase().startsWith('NEW HIRE')
  );

  // Debounced asset search
  useEffect(() => {
    const timer = setTimeout(() => {
      searchAssets(assetSearch);
    }, 300);
    return () => clearTimeout(timer);
  }, [assetSearch]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <p className="text-gray-500">Ticket not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-white border-b border-slate-200 shadow-sm">
        <div className="flex items-center justify-between px-6 py-3">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/tickets/managed')} className="gap-2">
              <ChevronLeft className="h-4 w-4" />
              Back
            </Button>
            <div>
              <h1 className="font-bold text-slate-800">Ticket #{ticket.ticket_number}</h1>
              <p className="text-xs text-slate-400">{ticket.created_by_email}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            {isNewHireTicket && !ticket.linked_onboarding_ticket && (
              <Button 
                variant="outline" size="sm" 
                onClick={handleStartOnboarding} 
                disabled={onboardParsing}
                className="text-emerald-600 hover:text-emerald-700 border-emerald-300"
                data-testid="start-onboarding-btn"
              >
                {onboardParsing ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <UserPlus className="h-4 w-4 mr-1" />}
                {onboardParsing ? 'Parsing...' : 'Onboard'}
              </Button>
            )}
            {ticket.linked_onboarding_ticket && (
              <Button 
                variant="outline" size="sm"
                onClick={() => navigate(`/tickets/managed/${ticket.linked_onboarding_ticket}`)}
                className="text-emerald-600 border-emerald-300"
                data-testid="view-onboarding-btn"
              >
                <UserPlus className="h-4 w-4 mr-1" />
                View Onboarding
              </Button>
            )}
            <Button variant="outline" size="sm" onClick={handleDelete} className="text-red-600 hover:text-red-700">
              <Trash2 className="h-4 w-4" />
            </Button>
            <Button onClick={handleUpdate} disabled={saving} className="gap-2">
              <Save className="h-4 w-4" />
              {saving ? 'Saving...' : 'Save Changes'}
            </Button>
          </div>
        </div>
      </header>

      <div className="p-6">
        {/* Merged ticket banners */}
        {ticket.status === 'Merged' && ticket.merged_into && (
          <div className="mb-4 p-3 bg-violet-50 border border-violet-200 rounded-lg flex items-center gap-3" data-testid="merged-into-banner">
            <GitMerge className="h-5 w-5 text-violet-600 flex-shrink-0" />
            <div className="text-sm">
              <span className="text-violet-800 font-medium">This ticket has been merged into </span>
              <button
                onClick={() => navigate(`/tickets/${ticket.merged_into}`)}
                className="text-violet-700 underline font-semibold hover:text-violet-900"
              >
                Ticket #{ticket.merged_into_number || '...'}
              </button>
            </div>
          </div>
        )}
        {ticket.merged_from && ticket.merged_from.length > 0 && (
          <div className="mb-4 bg-violet-50 border border-violet-200 rounded-lg overflow-hidden" data-testid="merged-from-banner">
            <div className="p-3 flex items-center gap-3">
              <GitMerge className="h-5 w-5 text-violet-600 flex-shrink-0" />
              <div className="text-sm text-violet-800">
                <span className="font-medium">{ticket.merged_from.length} ticket(s) merged into this ticket</span>
              </div>
            </div>
            {ticket.merged_from_details && ticket.merged_from_details.length > 0 && (
              <div className="border-t border-violet-200 divide-y divide-violet-100">
                {ticket.merged_from_details.map((child, idx) => (
                  <div
                    key={child.ticket_id || idx}
                    className="px-4 py-2 flex items-center justify-between hover:bg-violet-100/50 cursor-pointer transition-colors"
                    onClick={() => navigate(`/tickets/${child.ticket_id}`)}
                    data-testid={`merged-child-${child.ticket_number}`}
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <span className="text-sm font-medium text-violet-700">#{child.ticket_number}</span>
                      <span className="text-sm text-gray-700 truncate">{child.summary}</span>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {child.created_by_name && (
                        <span className="text-xs text-gray-500">{child.created_by_name}</span>
                      )}
                      <Badge className="text-xs bg-violet-100 text-violet-700">
                        was {child.status_before_merge}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Summary & Description */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between">
                  <span>{ticket.summary}</span>
                  <div className="flex gap-2">
                    <Badge className={`${STATUS_COLORS[ticket.status]}`}>{ticket.status}</Badge>
                    <Badge className={`${PRIORITY_COLORS[ticket.priority]}`}>{ticket.priority}</Badge>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="prose prose-sm max-w-none max-h-48 overflow-y-auto">
                  <p className="whitespace-pre-wrap text-gray-600">{ticket.description || 'No description provided.'}</p>
                </div>
                
                {/* Original ticket attachments - show prominently after description */}
                {ticket.attachments && ticket.attachments.length > 0 && (
                  <div className="mt-4 pt-4 border-t">
                    <div className="flex items-center gap-2 mb-3">
                      <Paperclip className="h-4 w-4 text-purple-600" />
                      <span className="font-medium text-sm text-purple-700">
                        Attachments ({ticket.attachments.length})
                      </span>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                      {ticket.attachments.map((att, idx) => {
                        const isImage = att.content_type?.startsWith('image/');
                        const attachmentUrl = `${process.env.REACT_APP_BACKEND_URL}/api/ticketing/managed/attachment/${att.attachment_id}`;
                        const FileIcon = getFileIcon(att.content_type, att.filename);
                        
                        return (
                          <a
                            key={idx}
                            href={attachmentUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="block border rounded-lg overflow-hidden hover:border-blue-400 hover:shadow-md transition-all group"
                            data-testid={`ticket-attachment-${att.attachment_id}`}
                          >
                            {isImage ? (
                              <div className="aspect-video bg-gray-100">
                                <img 
                                  src={attachmentUrl} 
                                  alt={att.filename}
                                  className="w-full h-full object-contain"
                                />
                              </div>
                            ) : (
                              <div className="aspect-video bg-gray-50 flex items-center justify-center">
                                <FileIcon className="h-12 w-12 text-gray-300 group-hover:text-blue-400" />
                              </div>
                            )}
                            <div className="p-2 bg-white border-t">
                              <p className="text-xs font-medium text-gray-700 truncate group-hover:text-blue-600">
                                {att.filename}
                              </p>
                              {att.size && (
                                <p className="text-xs text-gray-400">{formatFileSize(att.size)}</p>
                              )}
                            </div>
                          </a>
                        );
                      })}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Notes & Communication - Moved up */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  <MessageSquare className="h-4 w-4" />
                  Notes & Communication
                </CardTitle>
              </CardHeader>
              <CardContent>
                {/* Notes list */}
                <div className="space-y-4 mb-6 max-h-96 overflow-y-auto">
                  {ticket.notes && ticket.notes.length > 0 ? (
                    ticket.notes.map((note, idx) => (
                      <div 
                        key={idx} 
                        className={`p-3 rounded-lg border ${
                          note.source === 'email_reply' 
                            ? 'bg-blue-50 border-blue-200' 
                            : note.internal_only 
                              ? 'bg-yellow-50 border-yellow-200'
                              : 'bg-gray-50 border-gray-200'
                        }`}
                      >
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-sm">{note.author}</span>
                            {note.internal_only && (
                              <Badge className="text-xs bg-yellow-100 text-yellow-700">
                                Internal
                              </Badge>
                            )}
                            {note.sent_to && !note.internal_only && (
                              <Badge className="text-xs bg-green-100 text-green-700">
                                <Mail className="h-3 w-3 mr-1" />
                                Emailed
                              </Badge>
                            )}
                            {note.source === 'email_reply' && (
                              <Badge className="text-xs bg-blue-100 text-blue-700">
                                Email Reply
                              </Badge>
                            )}
                            {note.attachments && note.attachments.length > 0 && (
                              <Badge className="text-xs bg-gray-100 text-gray-600">
                                <Paperclip className="h-3 w-3 mr-1" />
                                {note.attachments.length}
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-gray-400">{formatDate(note.created_at)}</span>
                            {note.note_id && note.author_type !== 'system' && (
                              <button
                                onClick={() => handleDeleteNote(note.note_id)}
                                className="text-gray-300 hover:text-red-500 transition-colors"
                                title="Delete note"
                                data-testid={`delete-note-${note.note_id}`}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            )}
                          </div>
                        </div>
                        <p className="text-sm whitespace-pre-wrap">{note.content}</p>
                        {note.cc_emails && note.cc_emails.length > 0 && (
                          <p className="text-xs text-gray-400 mt-2">CC: {note.cc_emails.join(', ')}</p>
                        )}
                        
                        {/* Note attachments - display images inline */}
                        {note.attachments && note.attachments.length > 0 && (
                          <div className="mt-3 pt-3 border-t border-dashed">
                            <div className="grid grid-cols-2 gap-2">
                              {note.attachments.map((att, attIdx) => {
                                const isImage = att.content_type?.startsWith('image/');
                                const attachmentUrl = `${process.env.REACT_APP_BACKEND_URL}/api/ticketing/managed/attachment/${att.attachment_id}`;
                                const FileIcon = getFileIcon(att.content_type, att.filename);
                                
                                return isImage ? (
                                  <a
                                    key={attIdx}
                                    href={attachmentUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="block rounded overflow-hidden border hover:border-blue-400"
                                  >
                                    <img 
                                      src={attachmentUrl} 
                                      alt={att.filename}
                                      className="w-full max-h-48 object-contain bg-gray-50"
                                    />
                                    <div className="px-2 py-1 text-xs text-gray-500 bg-white border-t truncate">
                                      {att.filename}
                                    </div>
                                  </a>
                                ) : (
                                  <a
                                    key={attIdx}
                                    href={attachmentUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-2 px-3 py-2 bg-white border rounded hover:bg-gray-50 hover:border-blue-300"
                                  >
                                    <FileIcon className="h-5 w-5 text-gray-400" />
                                    <div className="flex-1 min-w-0">
                                      <p className="text-xs font-medium text-gray-700 truncate">{att.filename}</p>
                                      {att.size && <p className="text-xs text-gray-400">{formatFileSize(att.size)}</p>}
                                    </div>
                                    <Download className="h-4 w-4 text-gray-300" />
                                  </a>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    ))
                  ) : (
                    <p className="text-center text-gray-400 py-4">No notes yet</p>
                  )}
                </div>

                {/* Add Note */}
                <div className="border-t pt-4 space-y-3">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700">Add Response</span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleGenerateDraft}
                      disabled={generatingDraft}
                      className="text-xs gap-1 text-purple-600 border-purple-200 hover:bg-purple-50"
                    >
                      <Wand2 className="h-3 w-3" />
                      {generatingDraft ? 'Generating...' : 'AI Suggest'}
                    </Button>
                  </div>
                  <Textarea
                    data-testid="new-note-textarea"
                    placeholder="Add a note... (will be emailed to requester)"
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    rows={4}
                    className={draftResponse && newNote === draftResponse ? 'border-purple-300 bg-purple-50/30' : ''}
                  />
                  {draftResponse && newNote === draftResponse && (
                    <p className="text-xs text-purple-600 flex items-center gap-1">
                      <Sparkles className="h-3 w-3" />
                      AI-generated draft - review and edit before sending
                    </p>
                  )}
                  
                  {/* CC Recipients */}
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs text-gray-500">CC:</span>
                    {ccEmails.map((email, idx) => (
                      <Badge key={idx} className="text-xs bg-gray-100 cursor-pointer" onClick={() => setCcEmails(ccEmails.filter(e => e !== email))}>
                        {email} ×
                      </Badge>
                    ))}
                    <div className="relative">
                      <Input
                        className="w-48 h-7 text-xs"
                        placeholder="Add CC... (type & press Enter)"
                        value={ccInput}
                        onChange={(e) => {
                          setCcInput(e.target.value);
                          setShowCcDropdown(true);
                        }}
                        onFocus={() => setShowCcDropdown(true)}
                        onKeyDown={handleCcKeyDown}
                      />
                      {showCcDropdown && ccInput.length > 0 && (
                        <div className="absolute z-10 w-64 mt-1 bg-white border rounded-lg shadow-lg max-h-48 overflow-y-auto">
                          {azureUsers
                            .filter(u => 
                              u.mail && 
                              !ccEmails.includes(u.mail) &&
                              (u.displayName?.toLowerCase().includes(ccInput.toLowerCase()) ||
                               u.mail?.toLowerCase().includes(ccInput.toLowerCase()))
                            )
                            .slice(0, 10)
                            .map((user, idx) => (
                              <div
                                key={idx}
                                className="px-3 py-2 hover:bg-gray-100 cursor-pointer text-sm"
                                onClick={() => {
                                  setCcEmails([...ccEmails, user.mail]);
                                  setCcInput('');
                                  setShowCcDropdown(false);
                                }}
                              >
                                <div className="font-medium">{user.displayName}</div>
                                <div className="text-xs text-gray-500">{user.mail}</div>
                              </div>
                            ))
                          }
                          {azureUsers.filter(u => u.mail && !ccEmails.includes(u.mail)).length === 0 && (
                            <div className="px-3 py-2 text-sm text-gray-500">No users found</div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={internalOnly}
                          onChange={(e) => setInternalOnly(e.target.checked)}
                          className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                        />
                        Internal note only
                      </label>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => noteFileInputRef.current?.click()}
                        className="gap-1 text-gray-500 hover:text-gray-700"
                      >
                        <Paperclip className="h-4 w-4" />
                        Attach
                      </Button>
                      <input
                        ref={noteFileInputRef}
                        type="file"
                        multiple
                        onChange={handleNoteFileSelect}
                        className="hidden"
                        accept="image/*,.pdf,.doc,.docx,.xls,.xlsx,.txt,.csv,.zip"
                      />
                    </div>
                    
                    <Button 
                      size="sm" 
                      onClick={handleAddNote} 
                      disabled={!newNote.trim() || sendingNote}
                      className={`gap-2 ${internalOnly ? 'bg-gray-600 hover:bg-gray-700' : ''}`}
                      data-testid="send-note-btn"
                    >
                      {internalOnly ? (
                        <>
                          <MessageSquare className="h-4 w-4" />
                          {sendingNote ? 'Saving...' : 'Save Note'}
                        </>
                      ) : (
                        <>
                          <Send className="h-4 w-4" />
                          {sendingNote ? 'Sending...' : 'Send & Email'}
                        </>
                      )}
                    </Button>
                  </div>
                  
                  {/* Note Attachments Preview */}
                  {noteAttachments.length > 0 && (
                    <div className="mt-3 pt-3 border-t">
                      <div className="flex items-center gap-1 text-xs text-gray-500 mb-2">
                        <Paperclip className="h-3 w-3" />
                        {noteAttachments.length} file(s) to attach
                      </div>
                      <div className="flex flex-wrap gap-2">
                        {noteAttachments.map((file, idx) => (
                          <div key={idx} className="flex items-center gap-2 px-2 py-1 bg-gray-100 rounded text-sm">
                            <File className="h-3 w-3 text-gray-400" />
                            <span className="truncate max-w-32">{file.name}</span>
                            <button 
                              onClick={() => removeNoteAttachment(idx)}
                              className="text-gray-400 hover:text-red-500"
                            >
                              ×
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Duplicate Detection Card */}
            {duplicates && duplicates.duplicates?.length > 0 && (
              <Card className={duplicates.has_likely_duplicates ? "border-orange-300 bg-orange-50/50" : "border-blue-200 bg-blue-50/30"}>
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center justify-between">
                    <div className={`flex items-center gap-2 ${duplicates.has_likely_duplicates ? 'text-orange-700' : 'text-blue-700'}`}>
                      <Copy className="h-4 w-4" />
                      {duplicates.has_likely_duplicates ? 'Potential Duplicates Found' : 'Similar Tickets'}
                    </div>
                    <Badge className={duplicates.has_likely_duplicates ? 'bg-orange-100 text-orange-700' : 'bg-blue-100 text-blue-700'}>
                      {duplicates.duplicates.length} found
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {duplicates.duplicates.map((dup, idx) => (
                      <div 
                        key={idx}
                        className={`p-2 rounded border cursor-pointer hover:bg-white transition-colors ${
                          dup.is_likely_duplicate ? 'bg-orange-50 border-orange-200' : 'bg-white border-gray-200'
                        }`}
                        onClick={() => navigate(`/tickets/managed/${dup.ticket_id}`)}
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium">#{dup.ticket_number}</span>
                          <div className="flex items-center gap-2">
                            <Badge className={`text-xs ${STATUS_COLORS[dup.status] || 'bg-gray-100'}`}>
                              {dup.status}
                            </Badge>
                            <span className={`text-xs font-medium ${
                              dup.similarity_score >= 75 ? 'text-orange-600' : 
                              dup.similarity_score >= 50 ? 'text-blue-600' : 'text-gray-500'
                            }`}>
                              {dup.similarity_score}% match
                            </span>
                          </div>
                        </div>
                        <p className="text-xs text-gray-600 truncate">{dup.summary}</p>
                        <div className="flex items-center gap-2 mt-1 text-xs text-gray-400">
                          <span>By: {dup.created_by || 'Unknown'}</span>
                          {dup.assigned_to && <span>• Assigned: {dup.assigned_to}</span>}
                        </div>
                      </div>
                    ))}
                  </div>
                  {duplicates.has_likely_duplicates && (
                    <p className="text-xs text-orange-600 mt-3 flex items-center gap-1">
                      <AlertTriangle className="h-3 w-3" />
                      Consider merging or linking these tickets
                    </p>
                  )}
                </CardContent>
              </Card>
            )}

            {/* AI Guidance Card */}
            <Card className="border-purple-200 bg-purple-50/30">
              <CardHeader className="pb-2">
                <CardTitle className="text-sm flex items-center justify-between">
                  <div className="flex items-center gap-2 text-purple-700">
                    <Sparkles className="h-4 w-4" />
                    AI Guidance
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={handleCheckDuplicates}
                      disabled={checkingDuplicates}
                      className="text-xs h-7 text-blue-600 border-blue-200 hover:bg-blue-50"
                      data-testid="check-duplicates-btn"
                    >
                      <Copy className="h-3 w-3 mr-1" />
                      {checkingDuplicates ? 'Checking...' : 'Find Duplicates'}
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={handleAnalyzeWithAI}
                      disabled={analyzingWithAI}
                      className="text-xs h-7"
                      data-testid="analyze-ai-btn"
                    >
                      <Bot className="h-3 w-3 mr-1" />
                      {analyzingWithAI ? 'Analyzing...' : ticket.ai_guidance ? 'Re-analyze' : 'Analyze'}
                    </Button>
                  </div>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {ticket.ai_guidance ? (
                  <div className="space-y-3 text-sm">
                    {/* Suggested Category & Priority */}
                    <div className="flex gap-4">
                      {ticket.ai_guidance.suggested_category && (
                        <div>
                          <span className="text-xs text-gray-500">Suggested Category:</span>
                          <Badge variant="outline" className="ml-2 text-purple-700 border-purple-300">
                            {ticket.ai_guidance.suggested_category}
                          </Badge>
                        </div>
                      )}
                      {ticket.ai_guidance.suggested_priority && (
                        <div>
                          <span className="text-xs text-gray-500">Suggested Priority:</span>
                          <Badge className={`ml-2 ${PRIORITY_COLORS[ticket.ai_guidance.suggested_priority] || 'bg-gray-100'}`}>
                            {ticket.ai_guidance.suggested_priority}
                          </Badge>
                        </div>
                      )}
                    </div>
                    
                    {/* Troubleshooting Steps */}
                    {ticket.ai_guidance.troubleshooting_steps?.length > 0 && (
                      <div>
                        <div className="flex items-center gap-1 text-xs text-gray-500 mb-1">
                          <List className="h-3 w-3" />
                          Troubleshooting Steps:
                        </div>
                        <ol className="list-decimal list-inside space-y-1 text-gray-700 bg-white p-2 rounded border">
                          {ticket.ai_guidance.troubleshooting_steps.map((step, i) => (
                            <li key={i} className="text-xs">{step}</li>
                          ))}
                        </ol>
                      </div>
                    )}
                    
                    {/* Suggested Resolution */}
                    {ticket.ai_guidance.suggested_resolution && (
                      <div>
                        <div className="flex items-center gap-1 text-xs text-gray-500 mb-1">
                          <Lightbulb className="h-3 w-3" />
                          Suggested Resolution:
                        </div>
                        <p className="text-xs text-gray-700 bg-white p-2 rounded border">
                          {ticket.ai_guidance.suggested_resolution}
                        </p>
                      </div>
                    )}
                    
                    {/* Similar Tickets */}
                    {ticket.similar_tickets?.length > 0 && (
                      <div>
                        <div className="text-xs text-gray-500 mb-1">Similar Past Tickets:</div>
                        <div className="space-y-1">
                          {ticket.similar_tickets.map((st, i) => (
                            <div 
                              key={i}
                              onClick={() => navigate(`/tickets/managed/${st.ticket_id}`)}
                              className="text-xs bg-white p-1.5 rounded border cursor-pointer hover:bg-gray-50 truncate"
                            >
                              {st.summary}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-4 text-gray-500 text-sm">
                    <Bot className="h-8 w-8 mx-auto mb-2 text-gray-300" />
                    <p>Click "Analyze" to get AI-powered guidance</p>
                    <p className="text-xs text-gray-400 mt-1">Includes category, priority, troubleshooting steps, and similar tickets</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* AI Quality Review Scorecard - Only shown for closed tickets with reviews */}
            {(ticket.status === 'Resolved' || ticket.status === 'Closed') && (
              <AIQualityScorecard 
                ticket={ticket} 
                onReviewComplete={() => fetchTicket()}
              />
            )}

            {/* Knowledge Base Article Generation */}
            <Card className={ticket.kb_article_id ? "border-amber-200 bg-amber-50/30" : ""}>
              <CardContent className="py-4">
                {ticket.kb_article_id ? (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <BookOpen className="h-4 w-4 text-amber-600" />
                      <span className="text-sm font-medium text-amber-800">KB Article Linked</span>
                    </div>
                    <Button
                      size="sm"
                      variant="outline"
                      className="gap-1 text-amber-700 border-amber-300 hover:bg-amber-100"
                      onClick={() => navigate(`/kb/${ticket.kb_article_id}`)}
                      data-testid="view-kb-article-btn"
                    >
                      <ExternalLink className="h-3 w-3" />
                      View Article
                    </Button>
                  </div>
                ) : (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <BookOpen className="h-4 w-4 text-slate-400" />
                      <span className="text-sm text-slate-500">No KB article</span>
                    </div>
                    <Button
                      size="sm"
                      onClick={handleGenerateKB}
                      disabled={generatingKB}
                      className="gap-1 bg-amber-600 hover:bg-amber-700"
                      data-testid="generate-kb-btn"
                    >
                      {generatingKB ? (
                        <>
                          <Loader2 className="h-3 w-3 animate-spin" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <BookOpen className="h-3 w-3" />
                          Generate KB Article
                        </>
                      )}
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Suggested KB Articles */}
            {(kbSuggestions.length > 0 || loadingKBSuggestions) && (
              <Card className="border-blue-100" data-testid="kb-suggestions-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-blue-500" />
                    <span>Related KB Articles</span>
                    {loadingKBSuggestions && <Loader2 className="h-3 w-3 animate-spin text-blue-400" />}
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {kbSuggestions.map((s) => (
                    <div
                      key={s.kb_id}
                      className="group p-2 rounded-lg hover:bg-blue-50 transition-colors"
                      data-testid={`kb-suggestion-${s.kb_id}`}
                    >
                      <div className="flex items-start gap-3 cursor-pointer" onClick={() => navigate(`/kb/${s.kb_id}`)}>
                        <div className="flex-shrink-0 mt-0.5">
                          <div className={`w-9 h-9 rounded-full flex items-center justify-center text-xs font-bold
                            ${s.relevance >= 70 ? 'bg-green-100 text-green-700' :
                              s.relevance >= 40 ? 'bg-amber-100 text-amber-700' :
                              'bg-slate-100 text-slate-600'}`}
                          >
                            {s.relevance}%
                          </div>
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="text-sm font-medium text-slate-700 truncate group-hover:text-blue-700">
                            {s.title}
                          </p>
                          <div className="flex items-center gap-2 mt-0.5">
                            <span className="text-xs font-mono text-amber-600">{s.doc_id}</span>
                            {s.matched_terms?.length > 0 && (
                              <span className="text-xs text-slate-400 truncate">
                                {s.matched_terms.slice(0, 3).join(', ')}
                              </span>
                            )}
                          </div>
                        </div>
                        <ExternalLink className="h-3 w-3 text-slate-300 group-hover:text-blue-500 flex-shrink-0 mt-1" />
                      </div>
                      {/* Feedback buttons */}
                      <div className="flex items-center gap-1 mt-1.5 ml-12">
                        <span className="text-xs text-slate-400 mr-1">Helpful?</span>
                        <button
                          className={`p-1 rounded transition-colors ${kbFeedback[s.kb_id] === true ? 'bg-green-100 text-green-600' : 'text-slate-300 hover:text-green-500 hover:bg-green-50'}`}
                          onClick={(e) => { e.stopPropagation(); handleKBFeedback(s.kb_id, true); }}
                          data-testid={`kb-feedback-up-${s.kb_id}`}
                        >
                          <ThumbsUp className="h-3 w-3" />
                        </button>
                        <button
                          className={`p-1 rounded transition-colors ${kbFeedback[s.kb_id] === false ? 'bg-red-100 text-red-600' : 'text-slate-300 hover:text-red-500 hover:bg-red-50'}`}
                          onClick={(e) => { e.stopPropagation(); handleKBFeedback(s.kb_id, false); }}
                          data-testid={`kb-feedback-down-${s.kb_id}`}
                        >
                          <ThumbsDown className="h-3 w-3" />
                        </button>
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Linked Asset Card - Moved to bottom */}
            <Card className={linkedAsset ? "border-purple-200" : ""}>
              <CardHeader>
                <CardTitle className="text-sm flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Link2 className="h-4 w-4" />
                    Linked Asset
                  </div>
                  {linkedAsset ? (
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => navigate(`/assets/${linkedAsset.id}`)}
                        className="gap-1"
                      >
                        <ExternalLink className="h-3 w-3" />
                        View
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={handleUnlinkAsset}
                        className="text-red-600 hover:text-red-700 gap-1"
                      >
                        <Unlink className="h-3 w-3" />
                        Unlink
                      </Button>
                    </div>
                  ) : (
                    <Button 
                      size="sm" 
                      onClick={() => setShowLinkDialog(true)}
                      className="gap-1"
                    >
                      <Link2 className="h-3 w-3" />
                      Link Asset
                    </Button>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loadingAsset ? (
                  <div className="flex justify-center py-4">
                    <div className="animate-spin h-5 w-5 border-2 border-purple-500 border-t-transparent rounded-full"></div>
                  </div>
                ) : linkedAsset ? (
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {linkedAsset.status === 'Connected' || linkedAsset.online_status === 'SUCCESS' ? (
                          <Wifi className="h-5 w-5 text-green-500" />
                        ) : (
                          <WifiOff className="h-5 w-5 text-gray-400" />
                        )}
                        <div>
                          <p className="font-semibold">{linkedAsset.name}</p>
                          <p className="text-xs text-gray-500">{linkedAsset.serial}</p>
                        </div>
                      </div>
                      <Badge className={linkedAsset.status === 'Connected' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}>
                        {linkedAsset.status || linkedAsset.online_status}
                      </Badge>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div>
                        <span className="text-gray-500">OS</span>
                        <p className="font-medium">{linkedAsset.OS || linkedAsset.os || '-'}</p>
                      </div>
                      <div>
                        <span className="text-gray-500">IP Address</span>
                        <p className="font-medium font-mono">{linkedAsset.address || '-'}</p>
                      </div>
                      <div>
                        <span className="text-gray-500">User</span>
                        <p className="font-medium">{linkedAsset.user?.split('\\').pop() || '-'}</p>
                      </div>
                      <div>
                        <span className="text-gray-500">Manufacturer</span>
                        <p className="font-medium">{linkedAsset.manufacturer || '-'}</p>
                      </div>
                    </div>

                    {/* Status Alerts */}
                    {(linkedAsset.reboot_required === 'Yes' || 
                      (linkedAsset.missing_updates?.critical || 0) > 0 || 
                      (linkedAsset.vulnerabilities?.critical || 0) > 0) && (
                      <div className="flex flex-wrap gap-2 pt-2 border-t">
                        {linkedAsset.reboot_required === 'Yes' && (
                          <Badge className="bg-blue-100 text-blue-700 gap-1">
                            <RotateCcw className="h-3 w-3" />
                            Reboot Required
                          </Badge>
                        )}
                        {(linkedAsset.missing_updates?.critical || 0) > 0 && (
                          <Badge className="bg-orange-100 text-orange-700 gap-1">
                            <AlertTriangle className="h-3 w-3" />
                            {linkedAsset.missing_updates.critical} Critical Updates
                          </Badge>
                        )}
                        {(linkedAsset.vulnerabilities?.critical || 0) > 0 && (
                          <Badge className="bg-red-100 text-red-700 gap-1">
                            <AlertCircle className="h-3 w-3" />
                            {linkedAsset.vulnerabilities.critical} Vulnerabilities
                          </Badge>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-4 text-gray-400">
                    <Monitor className="h-8 w-8 mx-auto mb-2 opacity-50" />
                    <p className="text-sm">No asset linked to this ticket</p>
                    <p className="text-xs mt-1">Link an asset to track hardware-related issues</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Status & Assignment */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Ticket Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Status</label>
                  <Select value={editData.status || 'Open'} onValueChange={handleStatusChange}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Open">
                        <Badge className={`text-xs ${STATUS_COLORS['Open']}`}>Open</Badge>
                      </SelectItem>
                      <SelectItem value="In Progress">
                        <Badge className={`text-xs ${STATUS_COLORS['In Progress']}`}>In Progress</Badge>
                      </SelectItem>
                      <SelectItem value="Waiting">
                        <Badge className={`text-xs ${STATUS_COLORS['Waiting']}`}>Waiting</Badge>
                      </SelectItem>
                      <SelectItem value="Resolved">
                        <Badge className={`text-xs ${STATUS_COLORS['Resolved']}`}>Resolved</Badge>
                      </SelectItem>
                      <SelectItem value="Closed">
                        <Badge className={`text-xs ${STATUS_COLORS['Closed']}`}>Closed</Badge>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Assigned To</label>
                  <Select value={editData.assigned_to || 'unassigned'} onValueChange={(v) => setEditData(prev => ({ ...prev, assigned_to: v === 'unassigned' ? null : v }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Unassigned" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unassigned">Unassigned</SelectItem>
                      {settings?.it_staff?.filter(s => s && s.trim()).map(s => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Priority</label>
                  <Select value={editData.priority || 'Medium'} onValueChange={(v) => setEditData(prev => ({ ...prev, priority: v }))}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Low">
                        <Badge className={`text-xs ${PRIORITY_COLORS['Low']}`}>Low</Badge>
                      </SelectItem>
                      <SelectItem value="Medium">
                        <Badge className={`text-xs ${PRIORITY_COLORS['Medium']}`}>Medium</Badge>
                      </SelectItem>
                      <SelectItem value="High">
                        <Badge className={`text-xs ${PRIORITY_COLORS['High']}`}>High</Badge>
                      </SelectItem>
                      <SelectItem value="Critical">
                        <Badge className={`text-xs ${PRIORITY_COLORS['Critical']}`}>Critical</Badge>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Category</label>
                  <Input 
                    value={editData.category || ''} 
                    onChange={(e) => setEditData(prev => ({ ...prev, category: e.target.value }))}
                    placeholder="Category"
                  />
                </div>
              </CardContent>
            </Card>

            {/* Time Tracking */}
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-sm flex items-center gap-2">
                  <Clock className="h-4 w-4" />
                  Time Tracking
                </CardTitle>
                <Dialog open={showTimeDialog} onOpenChange={setShowTimeDialog}>
                  <DialogTrigger asChild>
                    <Button size="sm" variant="outline">
                      <Plus className="h-3 w-3 mr-1" />
                      Log Time
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Log Time</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                      <div>
                        <label className="text-sm text-gray-600 mb-1 block">Minutes</label>
                        <Input
                          type="number"
                          value={timeEntry.minutes}
                          onChange={(e) => setTimeEntry(prev => ({ ...prev, minutes: parseInt(e.target.value) || 0 }))}
                        />
                      </div>
                      <div>
                        <label className="text-sm text-gray-600 mb-1 block">Notes (optional)</label>
                        <Textarea
                          value={timeEntry.notes}
                          onChange={(e) => setTimeEntry(prev => ({ ...prev, notes: e.target.value }))}
                          placeholder="What did you work on?"
                        />
                      </div>
                      <Button onClick={handleLogTime} className="w-full">Log Time</Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </CardHeader>
              <CardContent>
                <div className="text-center py-4">
                  <p className="text-3xl font-bold text-gray-900">{formatTime(ticket.time_spent)}</p>
                  <p className="text-xs text-gray-500">Total Time Spent</p>
                  {(ticket.time_spent || 0) < 15 && ticket.status !== 'Resolved' && ticket.status !== 'Closed' && ticket.status !== 'Merged' && (
                    <p className="text-xs text-amber-600 mt-2 flex items-center justify-center gap-1" data-testid="min-time-warning">
                      <AlertTriangle className="h-3 w-3" />
                      Min. 15 min required to resolve
                    </p>
                  )}
                </div>
                {ticket.time_entries && ticket.time_entries.length > 0 && (
                  <div className="space-y-2 border-t pt-3 mt-3">
                    {ticket.time_entries.slice(-5).map((entry, idx) => (
                      <div key={idx} className="flex justify-between text-xs">
                        <span className="text-gray-500">{entry.notes || 'Time logged'}</span>
                        <span className="font-medium">{entry.minutes}m</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Metadata */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm">Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3 text-sm">
                <div className="space-y-1">
                  <label className="text-gray-500 text-xs">Created By</label>
                  <Input
                    value={editData.created_by_name || ''}
                    onChange={(e) => setEditData({ ...editData, created_by_name: e.target.value })}
                    placeholder="Requester name"
                    className="h-8 text-sm"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-gray-500 text-xs">Email</label>
                  <Input
                    value={editData.created_by_email || ''}
                    onChange={(e) => setEditData({ ...editData, created_by_email: e.target.value })}
                    placeholder="requester@email.com"
                    className="h-8 text-sm"
                    type="email"
                  />
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-500">Created On</span>
                  <span className="font-medium">{formatDate(ticket.created_on)}</span>
                </div>
                {ticket.closed_on && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Closed On</span>
                    <span className="font-medium">{formatDate(ticket.closed_on)}</span>
                  </div>
                )}
                {ticket.time_to_resolve && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Time to Resolve</span>
                    <span className="font-medium">{formatTime(ticket.time_to_resolve)}</span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* User Devices from Action1 */}
            <Card>
              <CardHeader>
                <CardTitle className="text-sm flex items-center gap-2">
                  <Monitor className="h-4 w-4" />
                  User's Devices
                </CardTitle>
              </CardHeader>
              <CardContent>
                {loadingDevices ? (
                  <div className="text-center py-2">
                    <div className="animate-spin h-5 w-5 border-2 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
                  </div>
                ) : userDevices.length === 0 ? (
                  <p className="text-xs text-gray-400 text-center py-2">No devices found</p>
                ) : (
                  <div className="space-y-2">
                    {userDevices.map((device, idx) => (
                      <div 
                        key={idx} 
                        className="p-2 bg-gray-50 rounded border text-xs cursor-pointer hover:bg-gray-100"
                        onClick={() => handleLinkAsset(device)}
                        title="Click to link this device"
                      >
                        <div className="flex items-center justify-between mb-1">
                          <span className="font-medium">{device.name}</span>
                          <div className="flex items-center gap-1">
                            {device.id === ticket.linked_asset_id && (
                              <Badge className="text-xs bg-purple-100 text-purple-700">Linked</Badge>
                            )}
                            {device.status === 'Connected' ? (
                              <Wifi className="h-3 w-3 text-green-500" />
                            ) : (
                              <WifiOff className="h-3 w-3 text-gray-400" />
                            )}
                          </div>
                        </div>
                        <div className="text-gray-500">{device.os}</div>
                        <div className="flex items-center gap-2 mt-1 text-gray-400">
                          <span>{device.address}</span>
                          {device.missing_updates?.critical > 0 && (
                            <span className="text-red-500 flex items-center gap-1">
                              <AlertTriangle className="h-3 w-3" />
                              {device.missing_updates.critical} critical
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Link Asset Dialog */}
      <Dialog open={showLinkDialog} onOpenChange={setShowLinkDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Link Asset to Ticket</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                className="pl-10"
                placeholder="Search by device name, serial, IP..."
                value={assetSearch}
                onChange={(e) => setAssetSearch(e.target.value)}
                autoFocus
              />
            </div>
            
            <div className="max-h-72 overflow-y-auto space-y-2">
              {searchingAssets ? (
                <div className="text-center py-4">
                  <div className="animate-spin h-5 w-5 border-2 border-purple-500 border-t-transparent rounded-full mx-auto"></div>
                </div>
              ) : assetResults.length > 0 ? (
                assetResults.map((asset) => (
                  <div
                    key={asset.id}
                    className="p-3 border rounded-lg hover:bg-purple-50 hover:border-purple-300 cursor-pointer transition-colors"
                    onClick={() => handleLinkAsset(asset)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        {asset.status === 'Connected' || asset.online_status === 'SUCCESS' ? (
                          <Wifi className="h-4 w-4 text-green-500" />
                        ) : (
                          <WifiOff className="h-4 w-4 text-gray-400" />
                        )}
                        <div>
                          <p className="font-medium">{asset.name}</p>
                          <p className="text-xs text-gray-500">{asset.serial} • {asset.address}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-gray-500">{asset.os}</p>
                        {asset.user && (
                          <p className="text-xs text-gray-400">{asset.user.split('\\').pop()}</p>
                        )}
                      </div>
                    </div>
                    {(asset.reboot_required === 'Yes' || (asset.missing_updates?.critical || 0) > 0) && (
                      <div className="flex gap-2 mt-2">
                        {asset.reboot_required === 'Yes' && (
                          <Badge className="text-xs bg-blue-100 text-blue-700">Reboot needed</Badge>
                        )}
                        {(asset.missing_updates?.critical || 0) > 0 && (
                          <Badge className="text-xs bg-orange-100 text-orange-700">
                            {asset.missing_updates.critical} updates
                          </Badge>
                        )}
                      </div>
                    )}
                  </div>
                ))
              ) : assetSearch.length >= 2 ? (
                <p className="text-center text-gray-400 py-4">No assets found matching "{assetSearch}"</p>
              ) : (
                <p className="text-center text-gray-400 py-4">Type at least 2 characters to search</p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLinkDialog(false)}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AI Resolution Note Dialog */}
      <Dialog open={showResolutionDialog} onOpenChange={setShowResolutionDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-purple-700">
              <Sparkles className="h-5 w-5" />
              Add Resolution Note
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-gray-600">
              You're marking this ticket as <Badge className={STATUS_COLORS[pendingStatusChange]}>{pendingStatusChange}</Badge>. 
              Add a resolution note to document what was done.
            </p>
            
            {generatingResolution ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin h-6 w-6 border-2 border-purple-500 border-t-transparent rounded-full mr-2"></div>
                <span className="text-sm text-gray-500">AI is generating a suggestion...</span>
              </div>
            ) : (
              <>
                {suggestedResolution && (
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                    <div className="flex items-center gap-1 text-xs text-purple-600 mb-1">
                      <Bot className="h-3 w-3" />
                      AI Suggested:
                    </div>
                    <p className="text-sm text-gray-700">{suggestedResolution}</p>
                  </div>
                )}
                
                <div>
                  <label className="text-sm font-medium mb-1 block">Resolution Note</label>
                  <Textarea
                    value={resolutionNote}
                    onChange={(e) => setResolutionNote(e.target.value)}
                    placeholder="Describe what was done to resolve this ticket..."
                    rows={4}
                    data-testid="resolution-note-input"
                  />
                  <p className="text-xs text-gray-400 mt-1">Edit the AI suggestion or write your own note</p>
                </div>
              </>
            )}
          </div>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={handleCancelResolution}>
              Cancel
            </Button>
            <Button 
              variant="outline"
              onClick={() => {
                setEditData(prev => ({ ...prev, status: pendingStatusChange }));
                setShowResolutionDialog(false);
                setPendingStatusChange(null);
              }}
            >
              Skip Note
            </Button>
            <Button 
              onClick={handleConfirmResolution}
              disabled={generatingResolution}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="confirm-resolution-btn"
            >
              <Sparkles className="h-4 w-4 mr-1" />
              Add Note & Resolve
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Onboarding from Ticket Dialog */}
      {showOnboardForm && (
        <div className="fixed inset-0 bg-black/40 flex items-start justify-center z-50 pt-12 overflow-y-auto" data-testid="onboard-from-ticket-dialog">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl mx-4 mb-12">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h2 className="text-lg font-semibold text-slate-800">Start Onboarding from Ticket #{ticket?.ticket_number}</h2>
              <button onClick={() => setShowOnboardForm(false)} className="text-slate-400 hover:text-slate-600">
                <XCircle className="h-5 w-5" />
              </button>
            </div>
            <div className="px-6 py-4 space-y-4 max-h-[70vh] overflow-y-auto">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-slate-500 block mb-1">Full Name *</label>
                  <input className="w-full border rounded px-3 py-2 text-sm" value={onboardForm.full_name} onChange={e => setOnboardForm({...onboardForm, full_name: e.target.value})} placeholder="Jane Doe" data-testid="ob-full-name" />
                </div>
                <div>
                  <label className="text-xs text-slate-500 block mb-1">Email Alias *</label>
                  <div className="flex items-center gap-1">
                    <input className="w-full border rounded px-3 py-2 text-sm" value={onboardForm.email_alias} onChange={e => setOnboardForm({...onboardForm, email_alias: e.target.value})} placeholder="jdoe" data-testid="ob-email-alias" />
                    <span className="text-xs text-slate-400 whitespace-nowrap">@gamerpackaging.com</span>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-slate-500 block mb-1">Job Title</label>
                  <input className="w-full border rounded px-3 py-2 text-sm" value={onboardForm.job_title} onChange={e => setOnboardForm({...onboardForm, job_title: e.target.value})} placeholder="Sales Representative" data-testid="ob-job-title" />
                </div>
                <div>
                  <label className="text-xs text-slate-500 block mb-1">Department</label>
                  <select className="w-full border rounded px-3 py-2 text-sm" value={onboardForm.department} onChange={e => setOnboardForm({...onboardForm, department: e.target.value})} data-testid="ob-department">
                    <option value="">Select department</option>
                    {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-slate-500 block mb-1">Manager</label>
                  <input className="w-full border rounded px-3 py-2 text-sm" value={onboardForm.manager} onChange={e => setOnboardForm({...onboardForm, manager: e.target.value})} placeholder="John Smith" data-testid="ob-manager" />
                </div>
                <div>
                  <label className="text-xs text-slate-500 block mb-1">Start Date</label>
                  <input type="date" className="w-full border rounded px-3 py-2 text-sm" value={onboardForm.start_date} onChange={e => setOnboardForm({...onboardForm, start_date: e.target.value})} data-testid="ob-start-date" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-slate-500 block mb-1">Office Location</label>
                  <input className="w-full border rounded px-3 py-2 text-sm" value={onboardForm.office_location} onChange={e => setOnboardForm({...onboardForm, office_location: e.target.value})} placeholder="Detroit" data-testid="ob-location" />
                </div>
                <div>
                  <label className="text-xs text-slate-500 block mb-1">Cell Phone</label>
                  <input className="w-full border rounded px-3 py-2 text-sm" value={onboardForm.cell_phone} onChange={e => setOnboardForm({...onboardForm, cell_phone: e.target.value})} placeholder="555-123-4567" data-testid="ob-cell-phone" />
                </div>
              </div>
              <div>
                <label className="text-xs text-slate-500 block mb-1">Personal Email</label>
                <input className="w-full border rounded px-3 py-2 text-sm" value={onboardForm.personal_email} onChange={e => setOnboardForm({...onboardForm, personal_email: e.target.value})} placeholder="jane@gmail.com" data-testid="ob-personal-email" />
              </div>
              <div className="border-t pt-4">
                <h3 className="text-sm font-medium text-slate-700 mb-3">Service Access</h3>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    ['bc', 'Business Central'], ['teams_phone', 'Teams Phone'], ['spiro', 'Spiro CRM'],
                    ['jd_edwards', 'JD Edwards'], ['business_cards', 'Business Cards'], ['website', 'Website'],
                  ].map(([key, label]) => (
                    <label key={key} className="flex items-center gap-2 p-2 rounded border text-sm cursor-pointer hover:bg-slate-50">
                      <input type="checkbox" checked={onboardForm[key]} onChange={e => setOnboardForm({...onboardForm, [key]: e.target.checked})} className="rounded" />
                      {label}
                    </label>
                  ))}
                </div>
              </div>
              {onboardForm.bc && (
                <div>
                  <label className="text-xs text-slate-500 block mb-1">BC Role (optional)</label>
                  <input className="w-full border rounded px-3 py-2 text-sm" value={onboardForm.bc_role} onChange={e => setOnboardForm({...onboardForm, bc_role: e.target.value})} placeholder="Auto-detected from department" data-testid="ob-bc-role" />
                </div>
              )}
            </div>
            <div className="flex items-center justify-end gap-2 px-6 py-4 border-t bg-slate-50 rounded-b-lg">
              <Button variant="outline" onClick={() => setShowOnboardForm(false)}>Cancel</Button>
              <Button onClick={handleSubmitOnboarding} disabled={onboardSubmitting} data-testid="ob-submit-btn">
                {onboardSubmitting ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <UserPlus className="h-4 w-4 mr-1" />}
                {onboardSubmitting ? 'Processing...' : 'Start Onboarding'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketDetailPage;
